/**
 * Append the current anonymousId to a URL pointing at the app, so the app's
 * cookie-handler can adopt the website's ID when its own UUID cookie is not
 * available (cross-site redirect, sameSite=strict drop, etc.). Returns the
 * input unchanged when:
 *   - SSR (no window)
 *   - no anonymousId is known yet
 *   - the URL already carries `aid`
 *   - `appOrigin` is provided and the URL's origin doesn't match it
 *   - the URL resolves to the same origin as the current page (cookie is
 *     already shared, no hand-off needed)
 *
 * Pass `appOrigin` whenever the caller knows it (e.g. `new URL(APP_URL).origin`).
 * The check is best-effort without it; the click-time interceptor still enforces
 * the same constraint for `<a>` clicks.
 */
export function withAnonymousId(href: string, appOrigin?: string): string {
  if (typeof window === 'undefined') return href;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const aid = (window as any).anonymousId as string | undefined;
  if (!aid) return href;
  try {
    const url = new URL(href, window.location.href);
    if (url.origin === window.location.origin) return href;
    if (appOrigin && url.origin !== appOrigin) return href;
    if (url.searchParams.has('aid')) return href;
    url.searchParams.set('aid', aid);
    return url.toString();
  } catch {
    return href;
  }
}
